import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  resendVerification,
  verifyEmail,
} from "../services/authApi";

const VerifyEmailLayer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || "";
  const emailDeliveryFailed =
    location.state?.emailDeliveryFailed === true;

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const verificationStarted = useRef(false);

  const [verificationStatus, setVerificationStatus] = useState(
    token ? "verifying" : "waiting"
  );
  const [verificationError, setVerificationError] = useState("");
  const [resendStatus, setResendStatus] = useState("");
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (!token || verificationStarted.current) {
      return;
    }

    verificationStarted.current = true;

    const runVerification = async () => {
      setVerificationStatus("verifying");
      setVerificationError("");

      try {
        await verifyEmail(token);
        setVerificationStatus("success");
        navigate("/dashboard", { replace: true });
      } catch (requestError) {
        setVerificationStatus("error");
        setVerificationError(
          requestError.message ||
            "This verification link is invalid or has expired."
        );
      }
    };

    runVerification();
  }, [token, navigate]);

  const handleResend = async () => {
    if (!email || isResending) {
      return;
    }

    setIsResending(true);
    setResendStatus("");

    try {
      const response = await resendVerification(email);

      setResendStatus(
        response?.message ||
          "If an unverified account exists for that email, a verification email will be sent."
      );
    } catch {
      setResendStatus(
        "We could not request another verification email. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  const renderVerificationContent = () => {
    if (token && verificationStatus === "verifying") {
      return (
        <>
          <h1>Verifying Your Email</h1>

          <p
            className="simplif-auth-subtitle"
            role="status"
          >
            Please wait while we verify your email address.
          </p>
        </>
      );
    }

    if (token && verificationStatus === "success") {
      return (
        <>
          <h1>Email Verified</h1>

          <p
            className="simplif-auth-subtitle"
            role="status"
          >
            Your email address has been verified successfully.
            You can now sign in to your Simplif account.
          </p>

          <Link
            to="/sign-in"
            className="simplif-auth-primary-button"
          >
            Sign in
          </Link>
        </>
      );
    }

    if (token && verificationStatus === "error") {
      return (
        <>
          <h1>Verification Failed</h1>

          <p
            className="simplif-auth-status-message"
            role="alert"
          >
            {verificationError}
          </p>

          <p className="simplif-auth-subtitle">
            If you requested more than one verification email,
            use the most recent link.
          </p>

          <Link
            to="/sign-in"
            className="simplif-auth-primary-button"
          >
            Return to Sign in
          </Link>
        </>
      );
    }

    return (
      <>
        <h1>Verify Your Email</h1>

        {emailDeliveryFailed ? (
          <p
            className="simplif-auth-status-message"
            role="alert"
          >
            Your account was created, but we could not send the
            verification email. You can request another email below.
          </p>
        ) : null}

        <p className="simplif-auth-subtitle">
          We sent a verification link to
          {email ? (
            <>
              <br />
              <strong>{email}</strong>
            </>
          ) : (
            " your email address"
          )}
          .
          <br />
          Open the email and follow the verification link to
          activate your account.
        </p>

        {email ? (
          <>
            <button
              type="button"
              className="simplif-auth-primary-button"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending
                ? "Sending..."
                : "Resend verification email"}
            </button>

            {resendStatus ? (
              <p
                className="simplif-auth-status-message"
                role="status"
              >
                {resendStatus}
              </p>
            ) : null}
          </>
        ) : (
          <p
            className="simplif-auth-status-message"
            role="status"
          >
            Need another verification email? Return to sign up or
            sign in to continue.
          </p>
        )}

        <div className="simplif-auth-bottom-link">
          Already verified?{" "}
          <Link to="/sign-in">
            Sign in
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className="simplif-auth-page">
      <header className="simplif-auth-header">
        <Link to="/sign-in" className="simplif-auth-logo">
          <img
            src="/assets/images/simplif/simplif-logo.png"
            alt="Simplif"
          />
        </Link>

        <div className="simplif-auth-header-actions">
          <Link to="/sign-in" className="simplif-auth-login-link">
            Login
          </Link>

          <Link to="/sign-up" className="simplif-auth-signup-button">
            SIGN UP
          </Link>
        </div>
      </header>

      <main className="simplif-auth-main">
        <div className="simplif-auth-card">
          <section className="simplif-auth-form-panel">
            <div className="simplif-auth-form-content">
              {renderVerificationContent()}
            </div>
          </section>

          <section className="simplif-auth-side-panel">
            <div className="simplif-auth-pattern simplif-auth-pattern-top" />

            <div className="simplif-auth-side-content">
              <h2>Return to Sign in</h2>

              <p>
                Already verified your email? Sign in to access your
                Simplif dashboard.
              </p>

              <Link
                to="/sign-in"
                className="simplif-auth-secondary-button"
              >
                Sign in
              </Link>
            </div>

            <div className="simplif-auth-pattern simplif-auth-pattern-bottom" />

            <div className="simplif-auth-support">
              <span className="simplif-auth-support-icon">?</span>
              <strong>Need Help?</strong> Support@simplif.com
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default VerifyEmailLayer;
